import { inject, Injectable } from '@angular/core';
import { Route, Routes } from '@angular/router';

import { environment } from '../../../environments/environment';
import { FeatureFlagNamesEnum } from '../../shared/constants/feature-flags.constants';
import {
  AdminPermissions, BusinessPermissions, ContractingStatusPermissions, CpcReportingPermissions, DataAggregatorPermissions,
  MarketingPermissions, ProductTrainingPermissions, RequestCenterPermissions,
} from '../constants/permissions-shorthand.constants';
import { authGuard } from '../guards/auth.guard';
import { featureFlagGuard } from '../guards/feature-flag.guard';
import { onboardingGuard } from '../guards/onboarding.guard';
import { permissionGuard } from '../guards/permission.guard';
import { UserContextService } from './user-context/user-context.service';

@Injectable({
  providedIn: 'root'
})
export class RouteConfigService {
  private readonly context = inject(UserContextService);

  /**
   * Build the full application route tree.
   * Must be called AFTER AppInitializationService.initialize() completes
   * so that feature flags are available.
   *
   * Routes are conditionally included based on feature flags:
   * - Sandbox: Non-production environments only (independent of flags)
   *
   * Routes that are not included simply don't exist in the config,
   * so direct URL access falls through to the catch-all → 404.
   */
  getRoutes(): Routes {

    return [
      // Standalone auth routes (no portal shell)
      {
        title: 'Service Unavailable',
        path: 'service-unavailable',
        loadComponent: () => import('../../shared/components/service-unavailable/service-unavailable.component').then(c => c.ServiceUnavailableComponent)
      },
      {
        title: 'Logout',
        path: 'log-out',
        loadComponent: () => import('../../features/auth/logout/logout.component').then(c => c.LogoutComponent)
      },
      {
        path: 'externals',
        loadChildren: () => import('../../features/externals/externals.routes').then(c => c.externalsRoutes),
      },
      {
        title: 'Onboarding',
        path: 'onboarding',
        canActivate: [authGuard],
        loadChildren: () => import('../../features/onboarding/onboarding.routes').then(c => c.onboardingRoutes)
      },

      // Full-screen carrier e-app wizard (no portal shell)
      {
        path: 'apply',
        canActivate: [
          authGuard,
          onboardingGuard,
          featureFlagGuard(FeatureFlagNamesEnum.showAtheneEApp),
          permissionGuard(BusinessPermissions.eApps),
        ],
        loadChildren: () => import('../../features/carrier-eapp/carrier-eapp.routes').then(c => c.carrierEappRoutes),
      },
      // All authenticated routes use the portal shell
      {
        path: '',
        loadComponent: () => import('../../shell/portal-shell.component').then(c => c.PortalShellComponent),
        canActivate: [authGuard, onboardingGuard],
        children: [
          {
            path: '',
            redirectTo: 'home',
            pathMatch: 'full',
          },
          ...this.getPortalChildRoutes(),
          {
            title: '404 Not Found',
            path: '404',
            loadComponent: () => import('../../features/404-not-found/404-not-found.component').then(c => c.NotFoundComponent)
          }
        ]
      },

      // Catch-all route
      {
        path: '**',
        redirectTo: 'home'
      }
    ];
  }

  /**
   * Build portal child routes based on feature flags.
   * - Sandbox / Clients: Non-production + non-beta only
   */
  private getPortalChildRoutes(): Route[] {
    const routes: Route[] = [
      {
        title: 'Home',
        path: 'home',
        loadComponent: () => import('../../features/dashboard/dashboard.component').then(c => c.DashboardComponent),
      },
      {
        title: 'Marketing',
        path: 'marketing',
        canActivate: [permissionGuard(MarketingPermissions, 'one', { allowArcUsers: true })],
        loadChildren: () => import('../../features/marketing/marketing.routes').then(c => c.marketing),
      },
      {
        title: 'Contracting',
        path: 'contracting',
        canActivate: [permissionGuard(ContractingStatusPermissions)],
        loadComponent: () => import('../../features/contracting/contracting.component').then(c => c.ContractingComponent),
      },
      {
        title: 'Product Training',
        path: 'training',
        canActivate: [permissionGuard(ProductTrainingPermissions)],
        loadChildren: () => import('../../features/training/training.routes').then(c => c.trainingRoutes),
      },
      {
        title: 'Resources',
        path: 'resources',
        loadChildren: () => import('../../features/resources/resources.routes').then(c => c.resourcesRoutes),
      },
      {
        title: 'Accounts',
        path: 'accounts',
        canActivate: [permissionGuard(BusinessPermissions.allBusiness)],
        loadChildren: () => import('../../features/accounts/accounts.routes').then(c => c.accounts),
      },
      {
        title: 'CPC Report',
        path: 'cpc-report',
        canActivate: [permissionGuard(CpcReportingPermissions)],
        loadComponent: () => import('../../features/cpc-reporting/cpc-reporting.component').then(c => c.CpcReportingComponent),
      },
      {
        title: 'Data Aggregator',
        path: 'data-aggregator',
        canActivate: [permissionGuard(DataAggregatorPermissions)],
        loadChildren: () => import('../../features/data-aggregator/data-aggregator.routes').then(c => c.dataAggregatorRoutes),
      },
      {
        title: 'Admin',
        path: 'admin',
        canActivate: [permissionGuard(AdminPermissions)],
        loadChildren: () => import('../../features/admin/admin.routes').then(c => c.adminRoutes),
      },
      {
        title: 'fOS',
        path: 'fos',
        canActivate: [featureFlagGuard(FeatureFlagNamesEnum.showFos)],
        loadChildren: () => import('../../features/fos/fos.routes').then(c => c.fosRoutes),
      },
      {
        title: 'Clients',
        path: 'clients',
        canActivate: [
          featureFlagGuard(FeatureFlagNamesEnum.showClients),
          permissionGuard(BusinessPermissions.newBusiness),
        ],
        loadChildren: () => import('../../features/clients/clients.routes').then(c => c.clientsRoutes),
      },
      {
        title: 'Request Center',
        path: 'requests',
        canActivate: [
          featureFlagGuard(FeatureFlagNamesEnum.showRequestCenter),
          permissionGuard(RequestCenterPermissions),
        ],
        loadChildren: () => import('../../features/request-center/request-center.routes').then(c => c.requestCenterRoutes),
      },
    ];

    // Integrations page (T20-3150): hidden from Production and Beta until sign-off.
    if (!environment.production && !environment.beta) {
      routes.push({
        title: 'Integrations',
        path: 'integrations',
        loadChildren: () => import('../../features/integrations/integrations.routes').then(c => c.integrationsRoutes),
      });

      // Athene Order Entry: DocuSign's return page. The API only serves Athene in Development and QA.
      routes.push({
        title: 'Athene',
        path: 'athene',
        loadChildren: () => import('../../features/athene/athene.routes').then(c => c.atheneRoutes),
      });
    }

    return routes;
  }
}